import Loading from '@/components/Loading';
import Measurements from '@/components/Subjects/Show/Measurements';
import FormComponent from '@/components/Subjects/Show/Measurements/Form/FormComponent';
import Deceased from '@/components/Subjects/Show/Notifications/Types/Deceased';
import Banner from '@/components/UI/Banner';
import GroupLabel from '@/components/UI/GroupLabel/GroupLabel';
import ApiErrorBanner from '@/components/ApiErrorBanner';
import { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner';
import { checkIsDeceased, getCalculationsAndMeasurements, hasOwnProperty } from '@/helpers';
import { _isEmpty, _isNotEmpty, _notNil } from '@/littledash';
import type { AnimalAlertV1 } from '@/model/Alert.model';
import type { Animal, AnimalApiId, DeceasedAnimal } from '@/model/Animal.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import Http from '@/support/http';
import { RouteService } from '@/support/RouteService';
import { Alert, AlertUpdateEventHandler } from '@/utils/alerts/Alert';
import React, { FC, useEffect, useMemo, useState, useCallback } from 'react';
import type { WorkflowModelChild } from '../Workflow.model';
import styles from './MeasurmentsView.module.scss';
import { WorkflowNoDataCard } from './WorkflowNoDataCard';
import { useSelector } from 'react-redux';
import { State } from '@/model/State.model.ts';
import {
  useAlerts,
  useMeasurements,
  useUpdateSubjectActions,
  useWorkflowSetup,
  useSavedValueDifference,
} from '@/utils/workflow/useWorkflow.tsx';
import { useMeasurementActions } from '@/utils/workflow/useMeasurementActions.tsx';
import { AlertService, AlertUpdateEvent } from '@/utils/alerts/useAlert.ts';
import { Study, StudyApiId } from '@/model/Study.model.ts';
import { TreatmentGroup } from '@/model/TreatmentGroup.model.ts';

const alertSettings = {
  displayHeader: false,
  displayDeferControl: false,
  displayTriggeredBy: false,
};

interface MeasurementViewProps {
  canWrite: boolean;
  closeModal: () => void;
  dispatch: React.Dispatch<unknown>;
  onSave: () => void;
  setSubject: React.Dispatch<React.SetStateAction<object>>;
  study: Study;
  collections: Array<{ id: string; name: string }>;
  subject: Animal;
  updateChecklist: (type: unknown, id: unknown) => React.Dispatch<unknown>;
  setUnsavedMeasurements: (unsaved: boolean) => void;
}

const MeasurementView: FC<MeasurementViewProps> = ({
  study,
  subject,
  collections,
  closeModal,
  updateChecklist,
  onSave,
  canWrite,
  setUnsavedMeasurements,
}) => {
  const [animalLoading, setAnimalLoading] = useState(false);
  const [apiError, setApiError] = useState<Error | null>(null);
  const { updateSingleSubject } = useUpdateSubjectActions();
  const { animalAlerts, updateAlerts } = useAlerts();
  const { animalMeasurements, todaysMeasurement, measurementsLoading } = useMeasurements(subject.id);
  const { fetchTodaysMeasurement, fetchMeasurements } = useMeasurementActions();
  const { workflowSetup } = useWorkflowSetup();
  const { clearSavedValueDifference } = useSavedValueDifference();

  const { measured_at, showGroups, playSound, workflowTabConfig } = {
    measured_at: '',
    showGroups: false,
    playSound: false,
    workflowTabConfig: [],
    ...(workflowSetup ?? {}),
  };
  const workflow = workflowTabConfig[0];
  const workflowMeasurementAudibleNotification = useSelector(
    (state: State) => state?.team?.features?.workflow_measurement_audible_notification ?? false
  );
  const playMeasurementNoise = workflowMeasurementAudibleNotification && (playSound ?? true);
  const cageName = useMemo(() => collections?.find(({ id }) => id === subject?.cage_id)?.name, [collections, subject]);
  const isDeceased = useMemo(() => checkIsDeceased(subject), [subject]);
  const isEnabled = canWrite && !isDeceased;

  const metrics = workflow.children?.filter((w: WorkflowModelChild) => w.value).map((w) => w.name) ?? [];

  const { invoke: fetchAnimalAlerts } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/animals/{animalApiId}/alerts',
    invokeOnInit: false,
    path: { studyApiId: study.api_id as StudyApiId, animalApiId: subject.api_id as AnimalApiId },
    options: { onError: { throw: false, capture: true, toast: false, slug: 'workflow-measurement-view-fetch-alerts' } },
  });

  const handleMeasurementSubmit = async (response: any, edited: boolean) => {
    const triggeredAlerts: Array<AnimalAlertV1> = response?.subject_alerts ?? [];

    // Clear saved value differences when editing existing measurements
    if (edited) {
      clearSavedValueDifference(subject.id);
      const alertsResponse = await fetchAnimalAlerts({
        path: { studyApiId: study.api_id as StudyApiId, animalApiId: subject.api_id as AnimalApiId },
      });
      if (alertsResponse.type === 'error') {
        setApiError(alertsResponse.error ?? new Error('Could not refresh animal alerts'));
      } else {
        const alerts = (alertsResponse.body as Array<AnimalAlertV1>) ?? [];
        updateAlerts({ type: 'RESET_ALERTS', animalId: subject.id, fetchedAlerts: alerts });
      }
    } else {
      updateAlerts({ type: 'ADD_ALERTS', newAlerts: triggeredAlerts });
    }

    closeModal();

    // If there are no critical alerts continue workflow
    if (!triggeredAlerts.some(({ notification }) => notification === 'critical')) {
      updateChecklist('measurement', subject.id);
      closeModal();
      // Assigning alt id updates animal and retrieves measurements
      if (workflow.onSave !== 'next-subject') {
        await fetchMeasurements(subject.id);
        await fetchTodaysMeasurement(subject.id, measured_at);
      }
      onSave();
    } else {
      await fetchMeasurements(subject.id);
      await fetchTodaysMeasurement(subject.id, measured_at);
    }
  };

  let studyGroup: TreatmentGroup | undefined;
  if (showGroups && !_isEmpty(study.study_groups) && hasOwnProperty(subject, 'study_group_id')) {
    studyGroup = study.study_groups.find((studyGroup: TreatmentGroup) => studyGroup.id === subject.study_group_id);
  }

  const handleDeceasedCallback = async () => {
    closeModal();
    await fetchAnimal();
  };

  const fetchAnimal = async () => {
    setAnimalLoading(true);

    const {
      data: { data },
    } = await Http.get(
      RouteService.legacyApi({
        apiRoute: 'studies.animal.show',
        path: { id: subject.id, studyId: study.id },
        query: { include: 'terminated_at_data,alerts' },
      }).url.href
    );
    if (_notNil(data['terminated_at'])) {
      data['deceasedCageId'] = subject.deceasedCageId;
    }
    updateSingleSubject({ ...subject, ...data });
    setAnimalLoading(false);
  };

  const calculationMap = useMemo(
    () => getCalculationsAndMeasurements(study?.settings?.calculations),
    [study?.settings?.calculations]
  );

  const handleAlertUpdate: AlertUpdateEventHandler = async (updateAction) => {
    switch (updateAction.type) {
      case 'resolved':
        if (_notNil(updateAction.data.resolved_at)) {
          updateAlerts({
            type: 'RESOLVE_ALERT',
            animalId: subject.id,
            alert: updateAction.data,
          });
        } else {
          updateAlerts({
            type: 'ADD_ALERTS',
            newAlerts: [updateAction.data],
          });
        }
        break;
      case 'error':
        setApiError(new Error('There was a problem updating the alert. Please try again.'));
        break;
    }
  };

  const alertDismissHandler = useCallback(
    (event: AlertUpdateEvent) => {
      const data = event.detail?.filter(({ subject_id }) => subject.id === subject_id) ?? [];
      if (_isNotEmpty(data.filter((alert) => alert.notification === 'critical'))) {
        updateAlerts({ type: 'ADD_ALERTS', newAlerts: data });
      }
    },
    [subject.id, updateAlerts]
  );

  const alertResolveHandler = useCallback(
    (event: AlertUpdateEvent) => {
      const data = event.detail?.filter(({ subject_id }) => subject.id === subject_id) ?? [];
      if (_isNotEmpty(data)) {
        data.forEach((alert) => {
          updateAlerts({ type: 'RESOLVE_ALERT', animalId: subject.id, alert: alert });
        });
      }
    },
    [subject.id, updateAlerts]
  );

  useEffect(() => {
    AlertService.addEventListener('alert-resolved', alertResolveHandler, { passive: true });
    AlertService.addEventListener('alert-dismissed', alertDismissHandler, { passive: true });
    return () => {
      AlertService.removeEventListener('alert-resolved', alertResolveHandler);
      AlertService.removeEventListener('alert-dismissed', alertDismissHandler);
    };
  }, [alertDismissHandler, alertResolveHandler]);

  if (animalLoading || measurementsLoading) {
    return <Loading />;
  }

  return (
    <div className="flex flex-wrap justify-between items-stetch h-100" data-testid="workflow-measurement-pane">
      <div className="w-50 pv2 ph3 br b--moon-gray bg-white">
        {apiError && (
          <ApiErrorBanner
            className="mb3"
            errorType={ApiErrorType.FETCH}
            error={apiError}
            onDismiss={() => setApiError(null)}
          />
        )}
        {isDeceased && (
          <Banner info className="mw6 mt3 mb3" dismiss={false}>
            <Deceased subject={subject as DeceasedAnimal} study={study} handleCallback={handleDeceasedCallback} />
          </Banner>
        )}
        {_isNotEmpty(animalAlerts?.[subject.id]) && (
          <div className={`flex flex-column g3 ${styles.alertsContainer}`}>
            {Object.values(animalAlerts[subject.id]).map((alert: AnimalAlertV1) => (
              <Alert
                key={alert.id}
                alert={alert}
                calculation={calculationMap.get(alert.calculation)}
                studyApiId={study.api_id as StudyApiId}
                animalApiId={subject.api_id as AnimalApiId}
                settings={alertSettings}
                onUpdate={handleAlertUpdate}
              />
            ))}
          </div>
        )}
        {isEnabled && (
          <div data-testid="workflow-measurement-form">
            <FormComponent
              key={`form-${subject.id}-${(todaysMeasurement?.updated_at as any)?.datetime}`}
              subject={subject}
              studyId={study.api_id}
              cageName={cageName ?? ''}
              preset={study.settings}
              onMeasurementSubmit={handleMeasurementSubmit}
              refetchAnimal={fetchAnimal}
              editing={false}
              todaysMeasurement={todaysMeasurement ?? undefined}
              percentChange={workflow?.percentChange}
              playMeasurementNoise={playMeasurementNoise}
              measurements={animalMeasurements}
              settings={{
                measured_at,
                measuring: metrics,
              }}
              assignAltId={workflow.assignAltId}
              altIdToAssign={workflow.altIdToAssign}
              setUnsavedMeasurements={setUnsavedMeasurements}
              cursorPosition={workflow.cursorPosition}
            />
          </div>
        )}
      </div>
      <div className="w-50 bg-white" data-testid="workflow-measurement-sidebar">
        {_notNil(studyGroup) && (
          <div className="pa3 mw6 pb0">
            <GroupLabel group={studyGroup} />
          </div>
        )}
        {animalMeasurements?.length ? (
          <Measurements
            subject={subject}
            study={study}
            measurements={animalMeasurements}
            onMeasurementSubmit={handleMeasurementSubmit}
            fromWorkflow={true}
          />
        ) : (
          WorkflowNoDataCard
        )}
      </div>
    </div>
  );
};

export default MeasurementView;
